'use client';

import { useTranslations } from 'next-intl';
import { Shield, FileText, Mail, AlertTriangle, Lock } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Card } from '@/components/ui/Card';
import { type Locale } from '@/lib/i18n/config';

interface DmcaPageClientProps {
  locale: Locale;
}

export default function DmcaPageClient({ locale }: DmcaPageClientProps) {
  const t = useTranslations();

  const dmcaHighlights = [
    {
      icon: Shield,
      title: t('dmca.highlights.respect.title'),
      description: t('dmca.highlights.respect.description'),
    },
    {
      icon: FileText,
      title: t('dmca.highlights.responsibility.title'),
      description: t('dmca.highlights.responsibility.description'),
    },
    {
      icon: AlertTriangle,
      title: t('dmca.highlights.action.title'),
      description: t('dmca.highlights.action.description'),
    },
    {
      icon: Lock,
      title: t('dmca.highlights.temporary.title'),
      description: t('dmca.highlights.temporary.description'),
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header locale={locale} />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[hsl(var(--color-primary)/0.1)] via-[hsl(var(--color-background))] to-[hsl(var(--color-secondary)/0.1)] py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
                <Shield className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                {t('dmca.title')}
              </h1>
              <p className="text-lg text-[hsl(var(--color-muted-foreground))]">
                {t('dmca.intro')}
              </p>
            </div>
          </div>
        </section>

        {/* Highlights */}
        <section className="py-12 bg-[hsl(var(--color-muted)/0.3)]">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {dmcaHighlights.map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card key={index} className="p-6 text-center" hover>
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
                      <Icon className="h-6 w-6 text-green-600" />
                    </div>
                    <h3 className="font-semibold mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-[hsl(var(--color-muted-foreground))]">
                      {item.description}
                    </p>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* DMCA Content */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto prose prose-lg">
              <p className="text-sm text-[hsl(var(--color-muted-foreground))] mb-8">
                {t('dmca.date')}
              </p>

              <h2>{t('dmca.sections.0.title')}</h2>
              <p>
                {t('dmca.sections.0.content')}
              </p>
              <br />

              <h2>{t('dmca.sections.1.title')} </h2>
              <p>
                {t('dmca.sections.1.content')}
              </p>
              <br />

              <h2>{t('dmca.sections.2.title')}</h2>
              <p>
                {t('dmca.sections.2.content.description')}
              </p>
              <ul>
                <li>{t('dmca.sections.2.content.requirements.0')}</li>
                <li>{t('dmca.sections.2.content.requirements.1')}</li>
                <li>{t('dmca.sections.2.content.requirements.2')}</li>
                <li>{t('dmca.sections.2.content.requirements.3')}</li>
                <li>{t('dmca.sections.2.content.requirements.4')}</li>
                <li>{t('dmca.sections.2.content.requirements.5')}</li>
              </ul>

              <p className="mt-4">
                {t('dmca.sections.2.content.sendTo')}
              </p>
              <p className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <strong>{t('dmca.contact.email')}</strong>
              </p>
              <br />

              <h2>{t('dmca.sections.3.title')}</h2>
              <ul>
                <li>{t('dmca.sections.3.content.0')}</li>
                <li>{t('dmca.sections.3.content.1')}</li>
                <li>{t('dmca.sections.3.content.2')}</li>
              </ul>
              <br />

              <h2>{t('dmca.sections.4.title')}</h2>
              <p>
                {t('dmca.sections.4.content')}
              </p>
              <br />

              <h2>{t('dmca.sections.5.title')}</h2>
              <p>
                {t('dmca.sections.5.content')}
              </p>
              <br />

              <h2>{t('dmca.sections.6.title')}</h2>
              <p>
                {t('dmca.sections.6.content')}
              </p>
              <br />

              <h2>{t('dmca.sections.7.title')}</h2>
              <p>
                {t('dmca.sections.7.content')}
                <br />
                <strong>{t('dmca.contact.email')}</strong>
              </p>
            </div>
          </div>
        </section>

        {/* Trust Badge */}
        <section className="py-12 bg-[hsl(var(--color-muted)/0.3)]">
          <div className="container mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-4 bg-green-50 border border-green-200 rounded-lg">
              <Shield className="h-8 w-8 text-green-600" />
              <div className="text-left">
                <p className="font-semibold text-green-800">
                  {t('dmca.trustBadge.title')}
                </p>
                <p className="text-sm text-green-600">
                  {t('dmca.trustBadge.description')}
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer locale={locale} />
    </div>
  );
}
